export class FlowchartModel {
    constructor(layout, root, vertices) {
        this.dfsCount = 0;
        this.maxRank = 100000000;
        this.layout = layout;
        this.vertexMapper = new Map();
        this.ranks = new Map();
        const internalVertices = [];
        this.createInternalCells(vertices, internalVertices);
        internalVertices.forEach((internalVertex) => {
            const edges = internalVertex.internalOutEdges;
            edges.forEach((internalEdge) => {
                const realEdges = internalEdge.edges;
                if (realEdges && realEdges.length > 0) {
                    const realEdge = realEdges[0];
                    let targetCell = this.layout.getVisibleTerminal(realEdge, false);
                    let internalTargetCell = this.vertexMapper.get(targetCell.id);
                    if (internalVertex === internalTargetCell) {
                        targetCell = this.layout.getVisibleTerminal(realEdge, true);
                        internalTargetCell = this.vertexMapper.get(targetCell.id);
                    }
                    if (internalTargetCell && internalVertex !== internalTargetCell) {
                        internalEdge.target = internalTargetCell;
                        if (internalVertex.internalInEdges.indexOf(internalEdge) === -1) {
                            internalTargetCell.internalInEdges.push(internalEdge);
                        }
                    }
                }
            });
        });
        this.rootNode = this.vertexMapper.get(root.id);
    }
    createInternalCells(vertices, internalVertices) {
        for (let j = 0; j < vertices.length; j++) {
            const vertex = vertices[parseInt(j.toString(), 10)];
            const internalVertex = {
                tempRank: -1, cell: vertex, hashCode: [], maxRank: null, minRank: null,
                id: vertex.id, internalOutEdges: [], internalInEdges: [], identicalSibling: []
            };
            internalVertices.push(internalVertex);
            this.vertexMapper.set(vertex.id, internalVertex);
            const connectors = this.layout.getEdges(vertex);
            let i = 0;
            while (i < connectors.length) {
                const connector = connectors[parseInt(i.toString(), 10)];
                const childVertex = this.layout.getVisibleTerminal(connector, false);
                if (childVertex !== vertex) {
                    const undirectedEdges = this.layout.getEdgesBetween(vertex, childVertex, true);
                    if (undirectedEdges.length > 0) {
                        const internalEdge = {
                            connectorIds: [], edges: undirectedEdges, ids: [], isReversed: false,
                            source: null, target: null, tempRank: 0, maxRank: null, minRank: null
                        };
                        for (let k = 0; k < undirectedEdges.length; k++) {
                            const undirectedEdge = undirectedEdges[parseInt(k.toString(), 10)];
                            //   if (!undirectedEdge.id) {
                            //     undirectedEdge.id = randomId();
                            //   }
                            internalEdge.ids.push(undirectedEdge.id);
                            if (connectors.indexOf(undirectedEdge) !== -1) {
                                if (connectors.indexOf(undirectedEdge) < i) {
                                    i--;
                                }
                                connectors.splice(connectors.indexOf(undirectedEdge), 1);
                            }
                        }
                        internalEdge.source = internalVertex;
                        if (internalVertex.internalOutEdges.indexOf(internalEdge) === -1) {
                            internalVertex.internalOutEdges.push(internalEdge);
                        }
                    }
                }
                else {
                    i++;
                }
            }
        }
    }
    //Initializes the ranks of the vertices
    /**
     * @Private
     * @returns { void }  Initializes the ranks of the vertices .\
     */
    layeringStage() {
        this.recycleConnectors();
        this.initialRank();
        this.fixRanks();
    }
    recycleConnectors() {
        const startNodes = [this.rootNode];
        this.visit(startNodes, true);
    }
    initialRank() {
        const startNodes = [this.rootNode];
        const internalNodes = Array.from(this.vertexMapper.values());
        while (startNodes.length > 0) {
            const internalNode = startNodes[0];
            const outEdges = internalNode.internalOutEdges;
            const inEdges = internalNode.internalInEdges;
            let allEdgesScanned = true;
            let minimumLayer = 100000000;
            for (let i = 0; i < inEdges.length; i++) {
                const internalEdge = inEdges[parseInt(i.toString(), 10)];
                if (internalEdge.tempRank === 5270620) {
                    // This edge has been scanned, get the layer of the node on the other end
                    const otherNode = internalEdge.source;
                    minimumLayer = otherNode.tempRank ? Math.min(minimumLayer, otherNode.tempRank - 1) : minimumLayer;
                }
                else {
                    allEdgesScanned = false;
                    break;
                }
            }
            // If all edge have been scanned, assign the layer, mark all edges in the other direction and remove from the nodes list
            if (allEdgesScanned) {
                internalNode.tempRank = minimumLayer;
                this.maxRank = Math.min(this.maxRank, minimumLayer);
                if (outEdges.length) {
                    if (internalNode.cell.isDecisionNode) {
                        const yesChild = outEdges.find((e) => e.target.cell.isYesChild);
                        const decisionNode = outEdges.find((e) => e.target.cell.isDecisionNode);
                        if (outEdges.indexOf(decisionNode) === -1 && outEdges.indexOf(yesChild) !== 0) {
                            outEdges.reverse();
                        }
                        if (this.layout.yesBranchDirection === 'RightInFlow'
                            || (this.layout.yesBranchDirection === 'SameAsFlow'
                                && this.layout.noBranchDirection === 'LeftInFlow')) {
                            outEdges.reverse();
                        }
                    }
                    for (let i = 0; i < outEdges.length; i++) {
                        const internalEdge = outEdges[parseInt(i.toString(), 10)];
                        internalEdge.tempRank = 5270620;
                        // Add node on other end of edge to LinkedList of nodes to be analysed
                        const otherNode = internalEdge.target;
                        // Only add node if it hasn't been assigned a layer
                        if (otherNode.tempRank === -1) {
                            // Mark this other node as neither being unassigned nor assigned
                            //so it isn't added to this list again, but it's layer isn't used in any calculation.
                            otherNode.tempRank = -2;
                            startNodes.push(otherNode);
                        }
                    }
                }
                startNodes.shift();
            }
            else {
                // Not all the edges have been scanned, get to the back of the class and put the dunces cap on
                const removedCell = startNodes.shift();
                startNodes.push(internalNode);
                if (removedCell === internalNode && startNodes.length === 1) {
                    // This is an error condition, we can't get out of this loop.
                    //It could happen for more than one node but that's a lot harder to detect. Log the error
                    break;
                }
            }
        }
        for (let i = 0; i < internalNodes.length; i++) {
            internalNodes[parseInt(i.toString(), 10)].tempRank -= this.maxRank;
        }
        let currentMaxLayer = 0;
        const layerDeterminingEdges = this.rootNode.internalOutEdges;
        for (let j = 0; j < layerDeterminingEdges.length; j++) {
            const internalEdge = layerDeterminingEdges[parseInt(j.toString(), 10)];
            const otherNode = internalEdge.target;
            this.rootNode.tempRank = (otherNode.tempRank !== undefined && otherNode.tempRank !== null) ?
                Math.max(currentMaxLayer, otherNode.tempRank + 1) : currentMaxLayer;
            currentMaxLayer = this.rootNode.tempRank;
        }
        this.maxRank = 100000000 - this.maxRank;
    }
    fixRanks() {
        const rankList = new Map();
        this.ranks = new Map();
        for (let i = 0; i <= this.maxRank; i++) {
            rankList.set(i, []);
            this.ranks.set(i, rankList.get(i));
        }
        const rootsArray = [this.rootNode];
        this.visit(rootsArray, false, rankList);
    }
    /**
     * used to visit all the entries on the given dictionary with given function \
     *
     * @returns { void }  used to visit all the entries on the given dictionary with given function .\
     * @param {InternalVertex[]} dfsRoots - provide the dfsRoots value.
     * @param {boolean} trackAncestors - provide the trackAncestors value.
     * @param {Map<number, []>} rankList - provide the rankList value.
     * @private
     */
    visit(dfsRoots, trackAncestors, rankList = null) {
        if (dfsRoots) {
            for (let i = 0; i < dfsRoots.length; i++) {
                const internalNode = dfsRoots[parseInt(i.toString(), 10)];
                if (internalNode) {
                    const seenNodes = new Map();
                    if (trackAncestors) {
                        internalNode.hashCode = [this.dfsCount, i];
                        this.extendedDfs(null, internalNode, null, seenNodes, i);
                    }
                    else {
                        this.depthFirstSearch(null, internalNode, null, seenNodes, rankList);
                    }
                }
            }
            this.dfsCount++;
        }
    }
    extendedDfs(parent, root, connectingEdge, seen, childHash) {
        if (parent) {
            if (!root.hashCode || root.hashCode[0] !== parent.hashCode[0]) {
                root.hashCode = [...parent.hashCode, childHash];
            }
        }
        const rootId = root.id;
        if (!seen.has(rootId)) {
            seen.set(rootId, root);
            this.removeConnectionEdge(parent, root, connectingEdge);
            const outgoingEdges = [...root.internalOutEdges];
            outgoingEdges.forEach((internalEdge, i) => {
                const targetNode = internalEdge.target;
                this.extendedDfs(root, targetNode, internalEdge, seen, i);
            });
        }
        else {
            this.removeConnectionEdge(parent, root, connectingEdge);
        }
    }
    removeConnectionEdge(parent, node, connectingEdge) {
        if (parent && this.isAncestor(node, parent)) {
            this.invert(connectingEdge);
            this.remove(connectingEdge, parent.internalOutEdges);
            parent.internalInEdges.push(connectingEdge);
            this.remove(connectingEdge, node.internalInEdges);
            node.internalOutEdges.push(connectingEdge);
        }
    }
    invert(edge) {
        const temp = edge.source;
        edge.source = edge.target;
        edge.target = temp;
        edge.isReversed = !edge.isReversed;
    }
    remove(edge, edges) {
        const index = edges.indexOf(edge);
        edges.splice(index, 1);
    }
    isAncestor(node, otherNode) {
        // Firstly, the hash code of this node needs to be shorter than the other node
        if (otherNode !== null && node.hashCode !== null && otherNode.hashCode !== null
            && node.hashCode.length < otherNode.hashCode.length) {
            if (node.hashCode === otherNode.hashCode) {
                return true;
            }
            for (let i = 0; i < node.hashCode.length; i++) {
                if (node.hashCode[parseInt(i.toString(), 10)] !== otherNode.hashCode[parseInt(i.toString(), 10)]) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }
    depthFirstSearch(parent, root, connectingEdge, seen, rankList) {
        const rootId = root.id;
        if (!seen.has(rootId)) {
            seen.set(rootId, root);
            this.updateMinMaxRank(parent, root, connectingEdge, 0, rankList);
            const outgoingEdges = [...root.internalOutEdges];
            outgoingEdges.forEach((internalEdge) => {
                const targetNode = internalEdge.target;
                this.depthFirstSearch(root, targetNode, internalEdge, seen, rankList);
            });
        }
        else {
            this.updateMinMaxRank(parent, root, connectingEdge, 1, rankList);
        }
    }
    updateMinMaxRank(parent, node, edge, seen, rankList) {
        const rankListArray = Array.from(rankList.values());
        if (node.maxRank == null && node.maxRank !== 0) {
            node.maxRank = -1;
        }
        if (node.minRank == null && node.minRank !== 0) {
            node.minRank = -1;
        }
        if (seen === 0 && node.maxRank < 0 && node.minRank < 0) {
            if (node.tempRank >= 0) {
                const rank = node.tempRank;
                rankListArray[parseInt(rank.toString(), 10)].push(node);
                node.maxRank = rank;
                node.minRank = rank;
                node.tempRank = rankListArray[node.maxRank].length - 1;
            }
        }
        if (parent !== null && edge !== null) {
            const parentToCellRankDifference = parent.maxRank - node.maxRank;
            if (parentToCellRankDifference > 1) {
                edge.maxRank = parent.maxRank;
                edge.minRank = node.maxRank;
                for (let i = edge.minRank + 1; i < edge.maxRank; i++) {
                    rankListArray[parseInt(i.toString(), 10)].push(edge);
                    // CheckMe
                    // this.layout.setTempVariable(edge, i, rankList[i].length - 1);
                }
            }
            else if (edge.isReversed) {
                edge.maxRank = parent.maxRank;
                edge.minRank = node.maxRank;
                for (let i = edge.minRank; i <= edge.maxRank; i++) {
                    rankListArray[parseInt(i.toString(), 10)].push(edge);
                }
            }
        }
    }
}
